import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../contexts/AuthContext";
import { useSidebar } from "../contexts/SidebarContext";
import UserProfile from "./UserProfile";
import NotificationBell from "../features/notifications/components/NotificationBell";
import GlobalSearch from "./GlobalSearch";

// Styled Components
const NavContainer = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  /* Mobile: account for safe area (notch); single-row header */
  padding-top: env(safe-area-inset-top, 0px);
  height: calc(4rem + env(safe-area-inset-top, 0px));
  background: white;
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border-bottom: 1px solid #e5e7eb;
  z-index: 1000;

  @media (min-width: 768px) {
    padding-top: 0;
    height: 4rem;
  }
`;

const NavContent = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 100%;
  padding: 0 0.75rem;
  width: 100%;
  gap: 0.5rem;
  min-width: 0; /* Allow flex items to shrink below content size */

  @media (min-width: 640px) {
    padding: 0 1.5rem;
    gap: 1rem;
  }

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const NavLeft = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  min-width: 0;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: 0.75rem;

  @media (min-width: 1024px) {
    margin-right: 1.5rem;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
`;

const LogoIcon = styled.div`
  width: 2rem;
  height: 2rem;
  background: #2563eb;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: white;
  }
`;

const LogoText = styled.span`
  margin-left: 0.5rem;
  font-size: 1.25rem;
  font-weight: bold;
  color: #111827;

  @media (max-width: 520px) {
    display: none;
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  @media (min-width: 768px) {
    gap: 1rem;
  }
`;

const NotificationBellWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const SearchSlot = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  justify-content: center;

  /* Icon-only: sit just before the right-side actions */
  @media (max-width: 1024px) {
    flex: 0 0 auto;
    margin-left: auto;
    justify-content: flex-end;
  }
`;

const MobileMenuButton = styled.button`
  display: block;
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0.375rem;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

// Sidebar Navigation
const Sidebar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: ${(props) => (props.$isCollapsed ? "80px" : "280px")};
  height: 100vh;
  background: white;
  border-right: 1px solid #e5e7eb;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  transform: translateX(${(props) => (props.$isOpen ? "0" : "-100%")});
  transition: all 0.3s ease-in-out;
  z-index: 999;
  overflow-y: auto;
  padding-top: calc(4rem + env(safe-area-inset-top, 0px));

  /* Show sidebar on tablet (768px+) and desktop */
  @media (min-width: 768px) {
    transform: translateX(0);
    position: fixed;
    padding-top: 4rem;
  }
`;

const SidebarOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
  opacity: ${(props) => (props.$isOpen ? "1" : "0")};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transition: all 0.3s ease-in-out;

  /* Hide overlay on tablet (768px+) and desktop */
  @media (min-width: 768px) {
    display: none;
  }
`;

const SidebarHeader = styled.div`
  padding: ${(props) =>
    props.$isCollapsed
      ? "1.5rem 0.75rem 1rem 0.75rem"
      : "1.5rem 1.5rem 1rem 1.5rem"};
  border-bottom: 1px solid #f3f4f6;
  display: ${(props) => (props.$isCollapsed ? "flex" : "block")};
  flex-direction: column;
  align-items: center;
  text-align: ${(props) => (props.$isCollapsed ? "center" : "left")};
`;

const SidebarTitle = styled.h2`
  font-size: ${(props) => (props.$isCollapsed ? "0.75rem" : "1.25rem")};
  font-weight: 700;
  color: #111827;
  margin: 0;
  white-space: ${(props) => (props.$isCollapsed ? "nowrap" : "normal")};
  overflow: ${(props) => (props.$isCollapsed ? "hidden" : "visible")};
  text-overflow: ${(props) => (props.$isCollapsed ? "ellipsis" : "unset")};
`;

const SidebarSubtitle = styled.p`
  font-size: ${(props) => (props.$isCollapsed ? "0.625rem" : "0.875rem")};
  color: #6b7280;
  margin: 0.25rem 0 0 0;
  display: ${(props) => (props.$isCollapsed ? "none" : "block")};
`;

const SidebarSection = styled.div`
  padding: 1rem 0;
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`;

const SidebarSectionTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.75rem ${(props) => (props.$isCollapsed ? "0.75rem" : "1.5rem")};
  text-align: ${(props) => (props.$isCollapsed ? "center" : "left")};
  display: ${(props) => (props.$isCollapsed ? "none" : "block")};
`;

const SidebarNavItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${(props) => (props.$isCollapsed ? "0" : "0.75rem")};
  padding: ${(props) => (props.$isCollapsed ? "0.75rem" : "0.75rem 1.5rem")};
  justify-content: ${(props) => (props.$isCollapsed ? "center" : "flex-start")};
  color: #374151;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  position: relative;
  min-height: 2.5rem;

  &:hover {
    background: #f9fafb;
    color: #111827;
  }

  &.active {
    background: #eff6ff;
    color: #1d4ed8;
    border-left-color: #3b82f6;
  }

  /* Tooltip for collapsed state */
  ${(props) =>
    props.$isCollapsed &&
    `
    &:hover::after {
      content: attr(data-tooltip);
      position: absolute;
      left: 100%;
      top: 50%;
      transform: translateY(-50%);
      background: #1f2937;
      color: white;
      padding: 0.5rem 0.75rem;
      border-radius: 0.375rem;
      font-size: 0.75rem;
      white-space: nowrap;
      z-index: 1000;
      margin-left: 0.5rem;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
  `}
`;

const SidebarIcon = styled.div`
  width: 1.25rem;
  height: 1.25rem;
  min-width: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: currentColor;
  flex-shrink: 0;
  opacity: 1;
  visibility: visible;

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const SidebarToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }

  @media (min-width: 1024px) {
    display: none;
  }
`;

const CollapseToggle = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  margin-left: 1rem;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }

  /* Show collapse toggle on tablet (768px+) and desktop */
  @media (min-width: 768px) {
    display: flex;
  }
`;

// Navigation items with permission checks and priority grouping
const navigationItems = [
  // Core Operations
  {
    name: "Dashboard",
    href: "/dashboard",
    permission: "reports:dashboard",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z"
        />
      </svg>
    ),
    show: true,
    priority: 1,
  },
  {
    name: "Leads",
    href: "/leads",
    permission: "leads:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
    show: true,
    priority: 1,
  },
  {
    name: "Conversations",
    href: "/conversations",
    permission: "leads:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
    ),
    show: true,
    priority: 1,
  },
  {
    name: "Quotes",
    href: "/quotes",
    permission: "quotes:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
    show: true,
    priority: 1,
  },
  {
    name: "Invoices",
    href: "/invoices",
    permission: "quotes:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
    show: true,
    priority: 1,
  },
  {
    name: "Finance",
    href: "/finance",
    permission: "quotes:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 10h18M5 10V7a2 2 0 012-2h10a2 2 0 012 2v3M5 10v9a2 2 0 002 2h10a2 2 0 002-2v-9"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 14h.01M12 14h.01M16 14h.01"
        />
      </svg>
    ),
    show: true,
    priority: 1,
  },
  {
    name: "Follow-ups",
    href: "/followups",
    permission: "followups:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    show: true,
    priority: 1,
  },
  {
    name: "Customers",
    href: "/customers",
    permission: "customers:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
    ),
    show: true,
    priority: 1,
  },
  {
    name: "Orders",
    href: "/orders",
    permission: "orders:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
        />
      </svg>
    ),
    show: true,
    priority: 1,
  },
  {
    name: "Trips",
    href: "/trips",
    permission: "orders:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
        />
      </svg>
    ),
    show: true,
    priority: 1,
  },

  // Customer Service
  {
    name: "Messages",
    href: "/messages",
    permission: "messages:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
        />
      </svg>
    ),
    show: true,
    priority: 2,
  },

  // Operations & Analytics
  {
    name: "Operations",
    href: "/operations",
    permission: "agents:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
        />
      </svg>
    ),
    show: true,
    priority: 3,
    section: "operations",
  },
  {
    name: "Vehicles",
    href: "/vehicles",
    permission: "agents:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    show: true,
    priority: 3,
    section: "operations",
  },
  {
    name: "Terms Management",
    key: "terms-management-ops",
    href: "/terms-management",
    permission: "system:settings",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 3l7 4v6c0 5-3 9-7 9s-7-4-7-9V7l7-4z"
        />
      </svg>
    ),
    show: true,
    priority: 3,
    section: "operations",
  },
  {
    name: "Notifications",
    href: "/notifications",
    permission: "notifications:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 17h5l-5 5v-5zM4.19 4.19A2 2 0 004 6v10a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-1.81 1.19z"
        />
      </svg>
    ),
    show: true,
    priority: 3,
    section: "operations",
  },
  {
    name: "Reports",
    href: "/reports",
    permission: "reports:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
    show: true,
    priority: 3,
    section: "analysis",
  },
  {
    name: "Ask Data",
    href: "/ask-data",
    permission: "reports:analytics",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 4v-4z"
        />
      </svg>
    ),
    show: true,
    priority: 3,
    section: "analysis",
  },
  {
    name: "Feedback",
    href: "/feedback",
    permission: "feedback:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      </svg>
    ),
    show: true,
    priority: 3,
    section: "analysis",
  },
];

// Admin-only items
const adminItems = [
  {
    name: "User Management",
    href: "/users",
    permission: "users:view",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
        />
      </svg>
    ),
    show: true,
    priority: 4,
  },
  {
    name: "Pricing",
    href: "/pricing/rate-cards",
    permission: "quotes:pricing",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    show: true,
    priority: 4,
  },
  {
    name: "System Settings",
    href: "/settings",
    permission: "system:settings",
    icon: (
      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    ),
    show: true,
    priority: 4,
  },
];

const Navigation = () => {
  const { hasPermission } = useAuth();
  const { isCollapsed, toggleCollapse } = useSidebar();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const allItems = [...navigationItems, ...adminItems].filter(
    (item) =>
      item.show &&
      Boolean(item.permission) &&
      hasPermission(item.permission)
  );

  // Group items by priority
  const coreItems = allItems.filter((item) => item.priority === 1);
  const serviceItems = allItems.filter((item) => item.priority === 2);
  const opsItems = allItems.filter((item) => item.priority === 3);
  const operationsItems = opsItems.filter((item) => item.section === "operations");
  const analysisItems = opsItems.filter((item) => item.section === "analysis");
  const adminOnlyItems = allItems.filter((item) => item.priority === 4);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const closeSidebar = () => setIsSidebarOpen(false);

  return (
    <>
      {/* Top Navigation Bar */}
      <NavContainer>
        <NavContent>
          <NavLeft>
            <SidebarToggle onClick={toggleSidebar}>
              <svg
                width="20"
                height="20"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </SidebarToggle>

            <LogoContainer>
              <LogoLink to="/dashboard">
                <LogoIcon>
                  <svg
                    width="20"
                    height="20"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </LogoIcon>
                <LogoText>Bubakii</LogoText>
              </LogoLink>
            </LogoContainer>
          </NavLeft>

          <SearchSlot>
            <GlobalSearch />
          </SearchSlot>

          <NavRight>
            <CollapseToggle
              onClick={toggleCollapse}
              title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              <svg
                width="20"
                height="20"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {isCollapsed ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                )}
              </svg>
            </CollapseToggle>
            {hasPermission("notifications:view") && (
              <NotificationBellWrapper>
                <NotificationBell />
              </NotificationBellWrapper>
            )}
            <UserProfile />
          </NavRight>
        </NavContent>
      </NavContainer>

      {/* Left Sidebar */}
      <Sidebar $isOpen={isSidebarOpen} $isCollapsed={isCollapsed}>
        <SidebarHeader $isCollapsed={isCollapsed}>
          <SidebarTitle $isCollapsed={isCollapsed}>Navigation</SidebarTitle>
          <SidebarSubtitle $isCollapsed={isCollapsed}>
            Manage your logistics platform
          </SidebarSubtitle>
        </SidebarHeader>

        {/* Core Operations */}
        <SidebarSection>
          <SidebarSectionTitle $isCollapsed={isCollapsed}>
            Core Operations
          </SidebarSectionTitle>
          {coreItems.map((item) => {
            const isActive =
              item.href === "/trips"
                ? location.pathname.startsWith("/trips")
                : item.href === "/finance"
                  ? location.pathname.startsWith("/finance")
                : location.pathname === item.href;
            return (
              <SidebarNavItem
                key={item.key || item.name}
                to={item.href}
                className={isActive ? "active" : ""}
                onClick={closeSidebar}
                $isCollapsed={isCollapsed}
                data-tooltip={isCollapsed ? item.name : undefined}
              >
                <SidebarIcon>{item.icon}</SidebarIcon>
                {!isCollapsed && item.name}
              </SidebarNavItem>
            );
          })}
        </SidebarSection>

        {/* Customer Service */}
        <SidebarSection>
          <SidebarSectionTitle $isCollapsed={isCollapsed}>
            Customer Service
          </SidebarSectionTitle>
          {serviceItems.map((item) => (
            <SidebarNavItem
              key={item.key || item.name}
              to={item.href}
              className={location.pathname === item.href ? "active" : ""}
              onClick={closeSidebar}
              $isCollapsed={isCollapsed}
              data-tooltip={isCollapsed ? item.name : undefined}
            >
              <SidebarIcon>{item.icon}</SidebarIcon>
              {!isCollapsed && item.name}
            </SidebarNavItem>
          ))}
        </SidebarSection>

        {/* Operations */}
        <SidebarSection>
          <SidebarSectionTitle $isCollapsed={isCollapsed}>
            Operations
          </SidebarSectionTitle>
          {operationsItems.map((item) => {
            // For Operations, check if pathname starts with /operations
            const isActive =
              item.href === "/operations"
                ? location.pathname.startsWith("/operations")
                : location.pathname === item.href;
            return (
              <SidebarNavItem
                key={item.key || item.name}
                to={item.href}
                className={isActive ? "active" : ""}
                onClick={closeSidebar}
                $isCollapsed={isCollapsed}
                data-tooltip={isCollapsed ? item.name : undefined}
              >
                <SidebarIcon>{item.icon}</SidebarIcon>
                {!isCollapsed && item.name}
              </SidebarNavItem>
            );
          })}
        </SidebarSection>

        {/* Analysis */}
        <SidebarSection>
          <SidebarSectionTitle $isCollapsed={isCollapsed}>
            Analysis
          </SidebarSectionTitle>
          {analysisItems.map((item) => (
            <SidebarNavItem
              key={item.key || item.name}
              to={item.href}
              className={location.pathname === item.href ? "active" : ""}
              onClick={closeSidebar}
              $isCollapsed={isCollapsed}
              data-tooltip={isCollapsed ? item.name : undefined}
            >
              <SidebarIcon>{item.icon}</SidebarIcon>
              {!isCollapsed && item.name}
            </SidebarNavItem>
          ))}
        </SidebarSection>

        {/* Administration */}
        {adminOnlyItems.length > 0 && (
          <SidebarSection>
            <SidebarSectionTitle $isCollapsed={isCollapsed}>
              Administration
            </SidebarSectionTitle>
            {adminOnlyItems.map((item) => (
              <SidebarNavItem
                key={item.key || item.name}
                to={item.href}
                className={location.pathname === item.href ? "active" : ""}
                onClick={closeSidebar}
                $isCollapsed={isCollapsed}
                data-tooltip={isCollapsed ? item.name : undefined}
              >
                <SidebarIcon>{item.icon}</SidebarIcon>
                {!isCollapsed && item.name}
              </SidebarNavItem>
            ))}
          </SidebarSection>
        )}
      </Sidebar>

      {/* Mobile Overlay */}
      <SidebarOverlay $isOpen={isSidebarOpen} onClick={closeSidebar} />
    </>
  );
};

export default Navigation;
